import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useInitConfigurationMutation } from "~/features/configurator/common/api/configuratorInitApi";
import { getAllConfiguratorDefinitions } from "~/bridges/configurator/configuratorRegistry";
import { transform } from "~/features/configurator/Linear/linearDataTransformer";
import { loadData } from "~/features/configurator/Linear/redux/actions";
import { trackLCStepStarted } from "~/features/configurator/common/configuratorGA";
import { XML_TRANSLATION_SHEETS } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import instance from "~/utils/i18n";
import type { ConfiguratorPageDefinition } from "~/features/configurator/ConfiguratorPageDefinition";
import type { ConfiguratorPageCustomization } from "~/features/configurator/common/components/ConfiguratorPageCustomizationContext";

export interface ConfiguratorPageViewModel {
  isLoading: boolean;
  error: string | null;
  configDefinition: ConfiguratorPageDefinition | null;
  customization: ConfiguratorPageCustomization | null;
}

export function useConfiguratorPageViewModel(productCode: string): ConfiguratorPageViewModel {
  const dispatch = useDispatch();
  const [initConfiguration, result] = useInitConfigurationMutation();

  const configDefinition = useMemo(
    () => getAllConfiguratorDefinitions().find((definition) => definition.productCode === productCode) ?? null,
    [productCode],
  );

  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    initConfiguration({ productCode })
      .unwrap()
      .then((payload) => {
        if (cancelled || !configDefinition) {
          return;
        }
        const model = transform(payload.data, configDefinition.type);
        dispatch(loadData(model, payload.helpUrl, configDefinition.type));

        const sheet = XML_TRANSLATION_SHEETS[configDefinition.type];
        const sheetPromise = sheet ? instance.loadNamespaces(sheet.sheetName) : Promise.resolve();

        void sheetPromise.then(() => {
          if (cancelled) {
            return;
          }
          if (model.highestVisitedStep === model.currentStep) {
            trackLCStepStarted(configDefinition.productCode, model.currentStep - 1);
          }
          setReady(true);
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [productCode, configDefinition, initConfiguration, dispatch]);

  const error = useMemo(() => {
    if (!configDefinition) {
      return "No configurator definition available!";
    }
    if (result.isError) {
      return "Configurator initialization failed.";
    }
    return null;
  }, [configDefinition, result.isError]);

  return {
    isLoading: result.isLoading || result.isUninitialized || !ready,
    error,
    configDefinition,
    customization: result.data?.customization ?? null,
  };
}
